import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import Icon from 'srcReact/components/icons/Icon'
import styles from './styles.module.scss'
import { useTranslation } from 'react-i18next'
import config from 'config'
import Modal from 'srcReact/components/common2/Modal'
import DeleteFileConfirmation from 'srcReact/components/popinsContent/DeleteFileConfirmation'
import {
  validatePDFMIMEType,
  getDescryptedMedia,
  getIconNameFromExtension,
  getNativeDecryptedMedia
} from 'srcReact/helpers/utils'
import { convertByteToMB, downloadMedia, openMedia } from 'srcReact/helpers/fileHelper'
import Spinner from 'srcReact/components/common2/Spinner'
import ReactMenu from 'srcReact/components/common2/ReactMenu'
import { appConstantSettings } from 'srcReact/appConstantSettings'

export default function DocumentPreview({
  fileName,
  extension,
  path,
  date,
  fileSize,
  documentNameMaxLength = null,
  showFileName = true,
  showOptions = false,
  showRemove = false,
  showDownload = false,
  showOpened = false,
  showDelete = false,
  showDate = false,
  handleRemove,
  handleDelete,
  className,
  isNative,
  deleteFileLoading,
  isCreator,
  documentNameCSS = '',
  mimeType,
  isOriginal,
  originalTag,
  decryptedFile = null,
  lastRef,
  documentId,
  linkUrl = null
}) {
  const { t } = useTranslation()
  const [showSpinner, setShowSpinner] = useState(false)
  const [isFileSizeExceed, setIsFileSizeExceed] = useState(false)

  const [showDeleteConfirmationModal, setShowDeleteConfirmationModal] = useState(false)

  useEffect(() => {
    if (fileSize) {
      const fileSizeInMB = convertByteToMB(fileSize)
      const hasExceed = fileSizeInMB > appConstantSettings.FILE_SIZE_LIMIT_FOR_PREVIEW
      setIsFileSizeExceed(hasExceed)
    }
  }, [fileSize])

  useEffect(() => {
    if (!deleteFileLoading && showDeleteConfirmationModal) {
      setShowDeleteConfirmationModal(false)
    }
  }, [deleteFileLoading])

  const downloadFile = (url, filename) => {
    if (decryptedFile) {
      download(decryptedFile)
    } else {
      setShowSpinner(true)
      getDescryptedMedia(url, isOriginal, originalTag, mimeType, documentId).then((data) => {
        setShowSpinner(false)
        // native decryption
        if (isNative) {
          getNativeDecryptedMedia(url, data.img, isOriginal, originalTag, mimeType, filename)
          return
        }
        downloadMedia(isOriginal, data.img, filename)
      })
    }
  }

  const openFile = (url, filename) => {
    const isValidPdf = validatePDFMIMEType({ mime_type: mimeType })
    if (showOpened && isValidPdf && (!isFileSizeExceed || isOriginal)) {
      getDescryptedMedia(url, isOriginal, originalTag, mimeType, documentId).then((data) => {
        // native decryption
        if (isNative) {
          getNativeDecryptedMedia(url, data.img, isOriginal, originalTag, mimeType, filename, 'open')
          return
        }
        openMedia(isOriginal, data.img, filename)
      })
    }
  }

  const openInPost = (linkUrl) => {
    if (linkUrl) {
      window.location = linkUrl
    }
  }

  const handleDeleteDocument = () => {
    setShowDeleteConfirmationModal(true)
  }

  const closeDeleteModal = () => {
    setShowDeleteConfirmationModal(false)
  }

  const options = () => {
    let actions = []
    actions.push({
      text: t('global.download'),
      onClick: () => downloadFile(path, fileName)
    })
    actions.push({
      text: t('global.openInPost'),
      onClick: () => openInPost(linkUrl)
    })
    return actions
  }

  return (
    <React.Fragment>
      <div className={`${styles.document} ${className}`} ref={lastRef}>
        {showDate && date && <span className={styles.date}>{date}</span>}
        {showRemove && (
          <div
            role='button'
            aria-label={t('messenger.removeFile', { filename: fileName })}
            className={styles.removeButton}
            onClick={handleRemove}>
            <Icon name='close24' size='large' />
          </div>
        )}
        <div
          role='button'
          className={`${styles.documentWithIcon} ${
            showOpened &&
            validatePDFMIMEType({ mime_type: mimeType }) &&
            (!isFileSizeExceed || isOriginal) &&
            styles.openFile
          }`}
          onClick={() => openFile(path, fileName)}>
          <Icon name={extension && getIconNameFromExtension(extension)} styleModifier={`${styles.documentIcon} `} />
          {showFileName && (
            <div className={`${styles.documentName} ${documentNameCSS}`}>
              {documentNameMaxLength && documentNameMaxLength > 0
                ? `${fileName.slice(0, documentNameMaxLength)}${fileName.length > documentNameMaxLength ? '...' : ''}`
                : fileName}
            </div>
          )}
        </div>
        {showDownload ? (
          showSpinner ? (
            <Spinner />
          ) : (
            <button
              type='button'
              id='native_image_download'
              onClick={() => downloadFile(path, fileName)}
              className={`${styles.actionButton} ${styles.downloadButton}`}>
              <Icon name='download24Coloured' />
            </button>
          )
        ) : null}

        {showDelete && isCreator && (
          <button className={styles.actionButton} onClick={handleDeleteDocument}>
            <Icon name='delete' />
          </button>
        )}
        {/* {showOptions && <Icon name='three_dots' styleModifier={styles.threeDotIcon} size='large' />} */}
        {showOptions && <ReactMenu options={options()} menuButtonCSS={styles.menuButtonCSS} menuCSS={styles.menuCSS} />}
      </div>
      {showDeleteConfirmationModal && (
        <Modal isOpen={showDeleteConfirmationModal} handleClose={closeDeleteModal}>
          <DeleteFileConfirmation
            fileName={fileName}
            handleClose={closeDeleteModal}
            handleDelete={handleDelete}
            deleteFileLoading={deleteFileLoading}
          />
        </Modal>
      )}
    </React.Fragment>
  )
}
